import React from "react"
import { useDispatch } from "react-redux"
import Head from "next/head"
import { VerticalTimeline } from "react-vertical-timeline-component"

import { Button, Container, Card, Col, Row } from "react-bootstrap"

import {
    toggleMakeEnquiryModal,
    setMakeEnquiryReqType,
} from "@/slices/makeEnquiryModalSlice"
import { toggleFindHomeModal } from "@/slices/findHomeModalSlice"

import MiscHeroImage from "@/main/miscHeroImage"
import CharacteristicsCarousel from "@/homeOwners/characteristicsCarousel"
import CharacteristicsStrip from "@/homeOwners/characteristicsStrip"
import TimelineElement from "@/homeOwners/timelineElement"
import InterestedCard from "@/homeOwners/interestedCard"
import ReviewCard from "@/homeOwners/reviewCard"

function RentersScreen() {
    const dispatch = useDispatch()

    const url = "https://webbjenkins.co.uk/tenants/"
    const title = "Renting a Property on the Isle of Wight - Webb Jenkins"
    const description =
        "Quality rentals are in high demand and can be hard to come by. Let us help you to get in early and find the right property at the right price for you."
    const imgUrl =
        "https://webb-jenkins-static-164679926.imgix.net/socials/webbjenkins-renting-social.jpg"
    const imgWidth = 1200
    const imgHeight = 630
    const imgAlt = "Webb Jenkins logo with renting a property caption"

    const description1 =
        "A local independent estate agency since 1998, our knowledge of the local area is unrivalled"
    const description2 =
        "Guiding you through the rental process, from finding your new home to helping you resolve maintenance issues"
    const description3 =
        "Active members of relevant bodies such as The Property Ombudsman Scheme and DPS, giving you peace of mind"
    const description4 =
        "Updating you at every step of the process so that you feel completely included and informed"

    return (
        <>
            <Head>
                <link rel="canonical" href={url} />
                <title>{title}</title>
                <meta name="description" content={description} />

                <meta property="fb:app_id" content="362437448786240" />
                <meta property="og:title" content={title} />
                <meta property="og:image:url" content={imgUrl} />
                <meta property="og:image:width" content={imgWidth} />
                <meta property="og:image:height" content={imgHeight} />
                <meta property="og:image:alt" content={imgAlt} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={url} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="en_GB" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image:src" content={imgUrl} />
                <meta name="twitter:site" content="@WebbJenkinsIOW" />
            </Head>
            <MiscHeroImage
                title="Renting a property"
                subtitle="The best rental properties are in demand. We let you know about suitable new properties right away, giving you the best chance of finding a home you love"
                image="couple-rent-property-resize.jpg"
                alt="Excited couple move into their new rented property"
                position="center"
                buttonText="Find me a home"
                buttonOnClick={() => dispatch(toggleFindHomeModal(true))}
            />

            <Container fluid="md" as={Card} className="card-overlap-2 p-0">
                <Card.Body className="px-0">
                    <Card.Title>
                        <h2 className="text-center">
                            What you can expect from us
                        </h2>
                    </Card.Title>
                </Card.Body>
            </Container>

            <Container fluid="md" as={Card} className="d-sm-none mt-4 p-0">
                <Card.Body className="px-0">
                    <CharacteristicsCarousel
                        description1={description1}
                        description2={description2}
                        description3={description3}
                        description4={description4}
                    />
                </Card.Body>
            </Container>

            <CharacteristicsStrip
                description1={description1}
                description2={description2}
                description3={description3}
                description4={description4}
            />

            <Container fluid="md" className=" mt-4">
                <Row>
                    <Col className="text-center">
                        <Button
                            size="lg"
                            variant="warning"
                            onClick={() => {
                                dispatch(toggleMakeEnquiryModal(true))
                                dispatch(setMakeEnquiryReqType("general"))
                            }}
                            className="px-4 rounded-0 "
                        >
                            <span className="text-bold text-white">
                                Make enquiry
                            </span>
                        </Button>
                    </Col>
                </Row>
            </Container>

            <Container fluid="md" as={Card} className="p-0 mt-5">
                <Card.Body>
                    <Card.Title>
                        <h2 className="text-center">The renting process</h2>
                    </Card.Title>
                </Card.Body>
            </Container>

            <Container fluid="md" className="mt-4">
                <VerticalTimeline
                    animate={false}
                    layout={"1-column-left"}
                    className="m-0 w-100 mw-100"
                >
                    <TimelineElement
                        position="left"
                        sequence={1}
                        image="tenants/rental-property-search.jpg"
                        title="Assist you in your rental property search"
                        description="We will help and assist you to find a suitable property for rental and advise of any necessary actions that you need to take."
                    />

                    <TimelineElement
                        position="right"
                        sequence={2}
                        image="tenants/rental-viewing.jpg"
                        title="Attend viewings"
                        description="All viewings will be accompanied by one of our professional team , answering any questions that you may have along the way."
                    />

                    <TimelineElement
                        position="left"
                        sequence={3}
                        image="tenants/property-rental-application.jpg"
                        title="Apply for a property"
                        description="Once a property has been found that meets your requirements, we will reference you to ensure suitability, take and lodge deposits, provide all necessary paperwork and documentation to comply with the most up to date regulations and requirements."
                    />

                    <TimelineElement
                        position="right"
                        sequence={4}
                        image="tenants/moving-in.jpg"
                        title="Moving in"
                        description="Prior to moving in we will ensure that all legal requirements have been met to include EICR report, gas safety checks and that your deposit is lodged correctly. At the landlords discretion we will arrange for a property inventory detailing the condition of the property that will be used for officially checking you in to the property."
                    />

                    <TimelineElement
                        position="left"
                        sequence={5}
                        image="tenants/property-maintenance.jpg"
                        title="Property management"
                        description="We will manage the property, handling all issues and enquiries, as well as passing your rent to the landlord. We will discuss any maintenance issues that you have with your landlord on your behalf in order to gain authorisation to complete repairs"
                    />
                </VerticalTimeline>
            </Container>
            <Container fluid="md">
                <Row className="justify-content-between d-flex">
                    <InterestedCard
                        title="Ready to find your new home?"
                        buttonTitle="Search properties"
                        transTypeId={2}
                    />
                    <ReviewCard
                        review="Thank you Beckie and Olivia for helping me rent my first ever property. It was a very daunting prospect having to find a property to rent for the first time, but I need not have worried. You looked after every aspect of the process for me and explained everything well and in simple terms which was brilliant. Thank you and I will certainly be recommending you to family and friends if they need help."
                        reviewer="Debbie"
                    />
                </Row>
            </Container>
        </>
    )
}

export default RentersScreen
